import * as React from "react";

import { useInfiniteQuery, useQueries, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { SCRIPT_GENERATION_DATASET } from "@/features/opportunities/constants/script-generation";
import {
  fetchHubOpportunities,
  type HubDocumentRow,
} from "@/features/persona-opportunities/api/persona-opportunity-hub.api";
import {
  HUB_PAGE_SIZE,
  hubDocumentGroupRowsQueryOptions,
  hubDocumentGroupsInfiniteQueryOptions,
  hubOpportunityGroupsInfiniteQueryOptions,
  hubOrdersInfiniteQueryOptions,
  hubTabCountsQueryOptions,
  personaOpportunitySetsInfiniteQueryOptions,
  sentScriptsQueryOptions,
} from "@/lib/queries";

import {
  sentScriptIdsByOpportunity,
  toHubChromeOpportunityRow,
  toHubChromeOrderRows,
  toHubChromeScriptGroup,
  toHubChromeScriptGroupSentItems,
  toHubChromeScriptRow,
} from "../../../lib/hub-row-mappers.js";
import { mergeHubSets } from "../../../lib/merge-hub-sets.js";
import { toPersonaOpportunitySet } from "../../../lib/persona-opportunity-set-mapper.js";
import type { PersonaOpportunitySet } from "../../../types/persona-opportunities.types.js";
import { PersonaOpportunityHubChrome, type PersonaOpportunityHubChromeProps } from "../PersonaOpportunityHubChrome/PersonaOpportunityHubChrome.js";
import { HUB_FILTER_ALL } from "../PersonaOpportunityHubChrome/hub-chrome-filters.js";
import {
  hubSelectionAllowsScriptGeneration,
  rememberSelectedRows,
} from "../PersonaOpportunityHubChrome/hub-fanout.js";
import type { OpportunityHubGroup } from "../PersonaOpportunityHubChrome/hub-chrome-grouping.js";
import type { HubOpportunityServerQuery } from "../PersonaOpportunityHubChrome/hub-opportunity-window.js";
import { sortScriptGroupRows } from "../PersonaOpportunityHubChrome/hub-script-table.js";
import { OpportunityHubOpportunityActions } from "./OpportunityHubOpportunityActions.js";
import { OpportunityGroupLoader, type OpportunityGroupSlice } from "./OpportunityGroupLoader.js";
import { OpportunityHubScriptActions } from "./OpportunityHubScriptActions.js";
import { OpportunityHubSetActions } from "./OpportunityHubSetActions.js";
import { useCompetitorScriptGate } from "./useCompetitorScriptGate.js";
import { useOpportunityHubScriptCreate } from "./useOpportunityHubScriptCreate.js";
import type { OpportunityHubListState } from "../PersonaOpportunityHubChrome/OpportunityHubListFooter.js";
import {
  clearHubSelectionLevel,
  selectOnlyHubSet,
  toggleHubSelection,
  toggleHubSelectionMembers,
  type OpportunityHubSelection,
  type OpportunityHubSelectionLevel,
} from "../PersonaOpportunityHubChrome/hub-chrome-selection.js";
import {
  DEFAULT_OPPORTUNITY_HUB_VIEWS,
  type HubChromeOpportunityRow,
  type HubChromeScriptGroup,
  type HubChromeSet,
  type OpportunityHubTabKey,
  type OpportunityHubViewMode,
  type OpportunityHubViews,
} from "../PersonaOpportunityHubChrome/hub-chrome.types.js";

export interface PersonaOpportunityHubProps {
  tenantId: string | null;
  sets: PersonaOpportunitySet[];
  activeTab: OpportunityHubTabKey;
  /** Shareable set scope; every other selection level is session state. */
  selectedSetIds: string[];
  canCreateSet?: boolean;
  isCreatingSet?: boolean;
  onCreateSet?: () => void;
  onStopSet?: (set: PersonaOpportunitySet) => void;
  onDeleteSet?: (set: PersonaOpportunitySet) => void;
  onRetrySet?: (set: PersonaOpportunitySet) => void;
  onRenameSet?: (set: PersonaOpportunitySet) => void;
  onRegenerateSet?: (set: PersonaOpportunitySet) => void;
  onTabChange: (tab: OpportunityHubTabKey) => void;
  onSelectedSetIdsChange: (setIds: string[]) => void;
  /**
   * Card open: set the shareable set scope and the landing tab in one
   * navigation. When omitted, the hub updates those two pieces separately.
   */
  onOpenScopedSet?: (setId: string, hubTab: OpportunityHubTabKey) => void;
  legacyFacets?: PersonaOpportunityHubChromeProps["legacyFacets"];
  onLegacyFacetsChange?: PersonaOpportunityHubChromeProps["onLegacyFacetsChange"];
  hubScriptsLiveRefetchMs?: number | false;
}

/**
 * Data side of the Opportunity Hub. Only the active tab asks for a page, and
 * opportunity children load for the groups that are actually on screen.
 */
export function PersonaOpportunityHub({
  tenantId,
  sets,
  activeTab,
  selectedSetIds,
  canCreateSet,
  isCreatingSet,
  onCreateSet,
  onStopSet,
  onDeleteSet,
  onRetrySet,
  onRenameSet,
  onRegenerateSet,
  onTabChange,
  onSelectedSetIdsChange,
  onOpenScopedSet,
  legacyFacets,
  onLegacyFacetsChange,
  hubScriptsLiveRefetchMs = false,
}: PersonaOpportunityHubProps) {
  const { t } = useTranslation();
  const [views, setViews] = React.useState<OpportunityHubViews>(DEFAULT_OPPORTUNITY_HUB_VIEWS);
  const [drilldown, setDrilldown] = React.useState<{
    opportunities: string[];
    scripts: string[];
  }>({ opportunities: [], scripts: [] });
  const [expandedScriptGroups, setExpandedScriptGroups] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [peekedScriptGroups, setPeekedScriptGroups] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [serverQuery, setServerQuery] = React.useState<HubOpportunityServerQuery | null>(null);
  const [neededKeys, setNeededKeys] = React.useState<readonly string[]>([]);
  const [expandGroupsRequest, setExpandGroupsRequest] = React.useState(0);
  const [groupLoadGeneration, setGroupLoadGeneration] = React.useState(0);
  const [sliceTick, setSliceTick] = React.useState(0);
  const slicesRef = React.useRef(new Map<string, OpportunityGroupSlice>());
  const fetchersRef = React.useRef(new Map<string, () => void>());

  const selection: OpportunityHubSelection = React.useMemo(
    () => ({ sets: selectedSetIds, ...drilldown }),
    [selectedSetIds, drilldown],
  );
  // Set scope lives in the URL. A back/forward or shared link can change it
  // without the in-hub reset that drops opportunity and script selections.
  const setScopeKey = [...selectedSetIds].sort().join("\0");
  const setScopeKeyRef = React.useRef(setScopeKey);
  React.useEffect(() => {
    if (setScopeKeyRef.current === setScopeKey) return;
    setScopeKeyRef.current = setScopeKey;
    setDrilldown((current) => {
      if (current.opportunities.length === 0 && current.scripts.length === 0) return current;
      return { opportunities: [], scripts: [] };
    });
  }, [setScopeKey]);

  const setsQuery = useInfiniteQuery(
    personaOpportunitySetsInfiniteQueryOptions(tenantId, { enabled: activeTab === "sets" }),
  );
  const tabCountsQuery = useQuery({
    ...hubTabCountsQueryOptions(tenantId, selectedSetIds),
    refetchInterval: hubScriptsLiveRefetchMs || false,
  });
  const groupIndexQuery = React.useMemo(
    () => (serverQuery ? withoutRowSort(serverQuery) : null),
    [serverQuery],
  );
  const groupsQuery = useInfiniteQuery({
    ...hubOpportunityGroupsInfiniteQueryOptions(
      tenantId,
      selectedSetIds,
      {
        groupBy: groupIndexQuery?.groupBy ?? "persona",
        filters: groupIndexQuery?.filters,
      },
      { enabled: activeTab === "opportunities" && serverQuery != null },
    ),
    refetchInterval: hubScriptsLiveRefetchMs || false,
  });
  const documentGroupsQuery = useInfiniteQuery({
    ...hubDocumentGroupsInfiniteQueryOptions(tenantId, selectedSetIds, drilldown.opportunities, {
      enabled: activeTab === "scripts",
    }),
    refetchInterval: hubScriptsLiveRefetchMs || false,
  });
  const documentGroups = React.useMemo(
    () => (documentGroupsQuery.data?.pages ?? []).flatMap((page) => page.groups),
    [documentGroupsQuery.data],
  );
  const loadedDocumentGroupIds = React.useMemo(
    () =>
      documentGroups
        .map((group) => group.opportunityId)
        .filter(
          (opportunityId) =>
            expandedScriptGroups.has(opportunityId) || peekedScriptGroups.has(opportunityId),
        ),
    [documentGroups, expandedScriptGroups, peekedScriptGroups],
  );
  const documentGroupRowQueries = useQueries({
    queries: loadedDocumentGroupIds.map((opportunityId) => ({
      ...hubDocumentGroupRowsQueryOptions(tenantId, opportunityId),
      enabled: Boolean(tenantId) && activeTab === "scripts",
      refetchInterval: hubScriptsLiveRefetchMs || false,
    })),
    combine: combineDocumentGroupRows,
  });
  const ordersQuery = useInfiniteQuery(
    hubOrdersInfiniteQueryOptions(tenantId, selectedSetIds, {
      enabled: activeTab === "sent_to_vendor",
    }),
  );
  const { data: sentScripts } = useQuery({
    ...sentScriptsQueryOptions(tenantId),
    enabled: Boolean(tenantId) && (activeTab === "scripts" || activeTab === "sent_to_vendor"),
  });

  // A sort change re-orders rows inside the loaded groups; resetting here would
  // blank the table and remount every group loader.
  const queryScopeKey = JSON.stringify({
    setIds: [...selectedSetIds].sort(),
    query: groupIndexQuery,
  });
  React.useEffect(() => {
    slicesRef.current = new Map();
    fetchersRef.current = new Map();
    setNeededKeys([]);
    setSliceTick((tick) => tick + 1);
    // The virtual window's needed-keys effect runs before this one and would
    // otherwise lose the request. The next generation asks again.
    setGroupLoadGeneration((generation) => generation + 1);
  }, [queryScopeKey]);

  const onOpportunityQueryChange = React.useCallback((query: HubOpportunityServerQuery) => {
    setServerQuery((current) =>
      JSON.stringify(current) === JSON.stringify(query) ? current : query,
    );
  }, []);

  const onSlice = React.useCallback((slice: OpportunityGroupSlice) => {
    const previous = slicesRef.current.get(slice.groupKey);
    if (
      previous &&
      previous.hasMore === slice.hasMore &&
      previous.isLoading === slice.isLoading &&
      previous.rows.length === slice.rows.length &&
      previous.rows.every((row, index) => row.id === slice.rows[index]?.id)
    ) {
      return;
    }
    slicesRef.current.set(slice.groupKey, slice);
    setSliceTick((tick) => tick + 1);
  }, []);

  const registerFetcher = React.useCallback((groupKey: string, fetchNext: () => void) => {
    fetchersRef.current.set(groupKey, fetchNext);
    return () => {
      if (fetchersRef.current.get(groupKey) === fetchNext) fetchersRef.current.delete(groupKey);
    };
  }, []);

  const onGroupsNeeded = React.useCallback((keys: readonly string[]) => {
    setNeededKeys((current) =>
      current.length === keys.length && current.every((key, index) => key === keys[index])
        ? current
        : keys,
    );
  }, []);

  const onLoadMoreGroup = React.useCallback((groupKey: string) => {
    fetchersRef.current.get(groupKey)?.();
  }, []);

  const hubSets = React.useMemo<HubChromeSet[]>(() => {
    const extra = (setsQuery.data?.pages ?? []).flatMap((page) =>
      page.items.map(toPersonaOpportunitySet),
    );
    return mergeHubSets(sets, extra);
  }, [sets, setsQuery.data]);

  const selectedSetsAreAllCustom = React.useMemo(() => {
    if (selection.sets.length === 0) return false;
    const selected = new Set(selection.sets);
    const selectedSets = hubSets.filter((set) => selected.has(set.id));
    return (
      selectedSets.length === selection.sets.length &&
      selectedSets.every((set) => set.type === "USER_GENERATED")
    );
  }, [hubSets, selection.sets]);

  const opportunityGroups = React.useMemo<OpportunityHubGroup[]>(() => {
    const summaries = (groupsQuery.data?.pages ?? []).flatMap((page) => page.groups);
    return summaries.map((summary) => ({
      key: summary.key,
      title: summary.title,
      avatarUrl: summary.avatarUrl,
      who: summary.who ?? null,
      personalityKey: summary.personalityKey ?? null,
      pains: summary.pains ?? [],
      motivations: summary.motivations ?? [],
      isStale: summary.isStale ?? false,
      setId: summary.setId ?? null,
      setName: summary.setName ?? null,
      rows: slicesRef.current.get(summary.key)?.rows ?? [],
      rollup: {
        count: summary.count,
        spend: summary.spend,
        creativesCount: summary.creativesCount,
        competitorsRunningCount: summary.competitorsRunningCount,
        competitorsTotal: summary.competitorsTotal,
        stickRatePct: summary.stickRatePct,
        duplicatedPct: summary.duplicatedPct,
        investmentIndex: summary.investmentIndex,
      },
    }));
  }, [groupsQuery.data, sliceTick]);

  const opportunities = React.useMemo<HubChromeOpportunityRow[]>(() => {
    const rows: HubChromeOpportunityRow[] = [];
    for (const slice of slicesRef.current.values()) rows.push(...slice.rows);
    return rows;
  }, [sliceTick]);

  const loadingKeys = React.useMemo(() => {
    const keys = new Set<string>();
    for (const [key, slice] of slicesRef.current) {
      if (slice.isLoading) keys.add(key);
    }
    return keys;
  }, [sliceTick]);

  const hasMoreByKey = React.useMemo(() => {
    const flags: Record<string, boolean> = {};
    for (const [key, slice] of slicesRef.current) flags[key] = slice.hasMore;
    return flags;
  }, [sliceTick]);

  const sentByOpportunity = React.useMemo(
    () => sentScriptIdsByOpportunity(sentScripts ?? {}),
    [sentScripts],
  );

  const scriptGroups = React.useMemo<HubChromeScriptGroup[]>(() => {
    const loaded = new Map(
      loadedDocumentGroupIds.map((opportunityId, index) => [
        opportunityId,
        documentGroupRowQueries[index],
      ]),
    );
    return documentGroups.map((group) => {
      const query = loaded.get(group.opportunityId);
      const loadedRows = query?.data
        ? sortScriptGroupRows(
            query.data.map((row) => toHubChromeScriptRow(row, sentScripts ?? {}, t)),
          )
        : null;
      const isExpanded = expandedScriptGroups.has(group.opportunityId);
      return toHubChromeScriptGroup(group, {
        rows: isExpanded ? (loadedRows ?? []) : [],
        isLoading: isExpanded && (query?.isLoading ?? false),
        sentItems: toHubChromeScriptGroupSentItems(
          sentByOpportunity.get(group.opportunityId) ?? [],
          loadedRows,
        ),
        isLoadingSentTitles: query?.isLoading ?? false,
      });
    });
  }, [
    documentGroups,
    loadedDocumentGroupIds,
    documentGroupRowQueries,
    expandedScriptGroups,
    sentByOpportunity,
    sentScripts,
    t,
  ]);

  const scripts = React.useMemo(
    () => scriptGroups.flatMap((group) => group.rows),
    [scriptGroups],
  );

  const toggleScriptGroup = React.useCallback((groupKey: string) => {
    setExpandedScriptGroups((current) => {
      const next = new Set(current);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  }, []);

  const peekScriptGroup = React.useCallback((groupKey: string) => {
    setPeekedScriptGroups((current) =>
      current.has(groupKey) ? current : new Set(current).add(groupKey),
    );
  }, []);

  const ensureScriptOpportunity = React.useCallback((opportunityId: string) => {
    setDrilldown((current) => {
      if (current.opportunities.includes(opportunityId)) return current;
      return { ...current, opportunities: [...current.opportunities, opportunityId] };
    });
    peekScriptGroup(opportunityId);
  }, [peekScriptGroup]);

  const scriptsById = React.useMemo(
    () => new Map(scripts.map((row) => [row.id, row])),
    [scripts],
  );

  const marketplaceOrders = React.useMemo(
    () => (ordersQuery.data?.pages ?? []).flatMap((page) => page.orders),
    [ordersQuery.data],
  );

  const orders = React.useMemo(
    () => marketplaceOrders.flatMap((order) => toHubChromeOrderRows(order, scriptsById)),
    [marketplaceOrders, scriptsById],
  );

  const exportOpportunities = React.useCallback(async () => {
    const rows: HubChromeOpportunityRow[] = [];
    let cursor: string | undefined;
    const seen = new Set<string>();
    for (;;) {
      const page = await fetchHubOpportunities({
        setIds: selectedSetIds,
        cursor,
        limit: HUB_PAGE_SIZE,
        filters: serverQuery?.filters,
      });
      rows.push(...page.items.map((row) => toHubChromeOpportunityRow(row, t)));
      if (!page.nextCursor || seen.has(page.nextCursor)) break;
      seen.add(page.nextCursor);
      cursor = page.nextCursor;
    }
    return rows;
  }, [selectedSetIds, serverQuery, t]);

  const listStates: Partial<Record<OpportunityHubTabKey, OpportunityHubListState>> = {
    sets: {
      isLoading: setsQuery.isLoading && sets.length === 0,
      isLoadingMore: setsQuery.isFetchingNextPage,
      hasMore: setsQuery.hasNextPage,
      onLoadMore: () => void setsQuery.fetchNextPage(),
    },
    opportunities: {
      isLoading: serverQuery == null || groupsQuery.isLoading,
      isLoadingMore: groupsQuery.isFetchingNextPage,
      hasMore: groupsQuery.hasNextPage,
      onLoadMore: () => void groupsQuery.fetchNextPage(),
    },
    scripts: {
      isLoading: documentGroupsQuery.isLoading,
      isLoadingMore: documentGroupsQuery.isFetchingNextPage,
      hasMore: documentGroupsQuery.hasNextPage,
      onLoadMore: () => void documentGroupsQuery.fetchNextPage(),
    },
    sent_to_vendor: {
      isLoading: ordersQuery.isLoading,
      isLoadingMore: ordersQuery.isFetchingNextPage,
      hasMore: ordersQuery.hasNextPage,
      onLoadMore: () => void ordersQuery.fetchNextPage(),
    },
  };

  const applySelection = (next: OpportunityHubSelection) => {
    setDrilldown({ opportunities: next.opportunities, scripts: next.scripts });
    if (next.sets !== selection.sets) onSelectedSetIdsChange(next.sets);
  };

  const handleToggleSelect = (level: OpportunityHubSelectionLevel, id: string) => {
    applySelection(toggleHubSelection(selection, level, id));
  };

  const handleOpenSet = (setId: string) => {
    setExpandGroupsRequest((request) => request + 1);
    const isCustomSet = hubSets.find((set) => set.id === setId)?.type === "USER_GENERATED";
    const landingTab: OpportunityHubTabKey = isCustomSet ? "scripts" : "opportunities";
    if (onOpenScopedSet) {
      setDrilldown({ opportunities: [], scripts: [] });
      onOpenScopedSet(setId, landingTab);
      return;
    }
    applySelection(selectOnlyHubSet(setId));
    onTabChange(landingTab);
  };

  const handleToggleMembers = (level: OpportunityHubSelectionLevel, ids: string[]) => {
    applySelection(toggleHubSelectionMembers(selection, level, ids));
  };

  const handleClearLevel = (level: OpportunityHubSelectionLevel) => {
    applySelection(clearHubSelectionLevel(selection, level));
  };

  const handleViewChange = (tab: OpportunityHubTabKey, view: OpportunityHubViewMode) => {
    setViews((current) => ({ ...current, [tab]: view }));
  };

  const selectedOpportunityCache = React.useRef(new Map<string, ReturnType<typeof toHubChromeOpportunityRow>>());
  const selectedOpportunityRows = rememberSelectedRows(
    selectedOpportunityCache.current,
    opportunities,
    selection.opportunities,
  );
  const rowsForGenerate = React.useMemo(() => {
    if (selectedOpportunityRows.length === 0) return opportunities;
    const byId = new Map(opportunities.map((row) => [row.id, row]));
    for (const row of selectedOpportunityRows) {
      if (!byId.has(row.id)) byId.set(row.id, row);
    }
    return [...byId.values()];
  }, [opportunities, selectedOpportunityRows]);
  const canGenerateHubScripts =
    !!canCreateSet &&
    hubSelectionAllowsScriptGeneration(rowsForGenerate, selection.opportunities);
  const generateBarWasOpener = React.useRef(false);
  const restoreGenerateBarFocus = React.useCallback(() => {
    if (!generateBarWasOpener.current) return;
    generateBarWasOpener.current = false;
    requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>('[data-testid="opportunity-hub-generate-scripts"]')
        ?.focus();
    });
  }, []);

  const scriptCreate = useOpportunityHubScriptCreate({
    tenantId,
    sets,
    selectedSetIds,
    canCreate: !!canCreateSet,
    onGenerated: () => {
      handleClearLevel("opportunities");
      onTabChange("scripts");
    },
    onOpportunityClosed: restoreGenerateBarFocus,
  });
  const openOpportunityGenerate = scriptCreate.openOpportunityGenerate;
  const scriptGate = useCompetitorScriptGate({
    rows: rowsForGenerate,
    onDismissed: restoreGenerateBarFocus,
    onGenerate: (targets) => openOpportunityGenerate?.(targets),
    onShowTopics: () => {
      // The bar unmounts with the cleared selection, so do not move focus to it.
      generateBarWasOpener.current = false;
      handleClearLevel("opportunities");
      onLegacyFacetsChange?.({
        action: legacyFacets?.action ?? HUB_FILTER_ALL,
        dataset: SCRIPT_GENERATION_DATASET,
        flowName: SCRIPT_GENERATION_DATASET,
        feature: HUB_FILTER_ALL,
        subFeature: HUB_FILTER_ALL,
        engine: legacyFacets?.engine ?? HUB_FILTER_ALL,
      });
    },
  });

  return (
    <>
      {activeTab === "opportunities" && serverQuery
        ? neededKeys.map((groupKey) => (
            <OpportunityGroupLoader
              key={`${queryScopeKey}:${groupKey}`}
              tenantId={tenantId}
              setIds={selectedSetIds}
              query={serverQuery}
              groupKey={groupKey}
              onSlice={onSlice}
              registerFetcher={registerFetcher}
            />
          ))
        : null}
      <PersonaOpportunityHubChrome
        sets={hubSets}
        opportunities={opportunities}
        scripts={scripts}
        scriptWindow={{
          groups: scriptGroups,
          expandedKeys: expandedScriptGroups,
          onToggleGroup: toggleScriptGroup,
          onPeekGroup: peekScriptGroup,
        }}
        orders={orders}
        marketplaceOrders={marketplaceOrders}
        listStates={listStates}
        selection={selection}
        activeTab={activeTab}
        tabCounts={tabCountsQuery.data ?? null}
        views={views}
        canCreateSet={canCreateSet}
        isCreatingSet={isCreatingSet}
        onCreateSet={onCreateSet}
        onCreateScript={scriptCreate.openCreate}
        onStopSet={onStopSet}
        onDeleteSet={onDeleteSet}
        onRetrySet={onRetrySet}
        onRenameSet={onRenameSet}
        onRegenerateSet={onRegenerateSet}
        onTabChange={onTabChange}
        tenantId={tenantId}
        sentScripts={sentScripts ?? {}}
        onToggleSelect={handleToggleSelect}
        onOpenSet={handleOpenSet}
        onToggleMembers={handleToggleMembers}
        onClearLevel={handleClearLevel}
        onViewChange={handleViewChange}
        opportunityWindow={{
          groups: opportunityGroups,
          isLoading: serverQuery == null || groupsQuery.isLoading,
          isLoadingMoreGroups: groupsQuery.isFetchingNextPage,
          hasMoreGroups: groupsQuery.hasNextPage,
          loadingKeys,
          hasMoreByKey,
        }}
        onOpportunityQueryChange={onOpportunityQueryChange}
        expandGroupsRequest={expandGroupsRequest}
        groupLoadGeneration={groupLoadGeneration}
        onOpportunityGroupsNeeded={onGroupsNeeded}
        onLoadMoreOpportunityGroups={() => void groupsQuery.fetchNextPage()}
        onLoadMoreOpportunityGroup={onLoadMoreGroup}
        onExportOpportunities={exportOpportunities}
        onEnsureScriptOpportunity={ensureScriptOpportunity}
        onGenerateOpportunityScripts={
          openOpportunityGenerate
            ? (row) => {
                scriptGate.request([
                  {
                    setId: row.setId,
                    setType: row.setType,
                    angles: [
                      {
                        id: row.id,
                        angle: row.angle,
                        ...(row.personaName ? { personaTitle: row.personaName } : {}),
                        supportingAd: row.thumbnailUrl
                          ? { thumbnailUrl: row.thumbnailUrl }
                          : null,
                      },
                    ],
                  },
                ]);
              }
            : undefined
        }
        scopedSetIds={selectedSetIds}
        legacyFacets={legacyFacets}
        onLegacyFacetsChange={onLegacyFacetsChange}
      />
      {scriptCreate.dialogs}
      {scriptGate.dialog}
      {activeTab === "opportunities" ? (
        <OpportunityHubOpportunityActions
          rows={rowsForGenerate}
          selectedIds={selection.opportunities}
          canGenerate={canGenerateHubScripts}
          onGenerate={(targets) => {
            generateBarWasOpener.current = true;
            scriptGate.request(targets);
          }}
          onClearSelection={() => handleClearLevel("opportunities")}
        />
      ) : null}
      {activeTab === "sets" ? (
        <OpportunityHubSetActions
          selectedIds={selection.sets}
          nextTab={selectedSetsAreAllCustom ? "scripts" : "opportunities"}
          onClearSelection={() => handleClearLevel("sets")}
          onGoTo={onTabChange}
        />
      ) : null}
      {activeTab === "scripts" ? (
        <OpportunityHubScriptActions
          tenantId={tenantId}
          rows={scripts}
          selectedIds={selection.scripts}
          canEdit={!!canCreateSet}
          sentScripts={sentScripts ?? {}}
          onClearSelection={() => handleClearLevel("scripts")}
        />
      ) : null}
    </>
  );
}

/** The group index endpoint ignores `sort`; only child rows are ordered by it. */
function withoutRowSort(query: HubOpportunityServerQuery): HubOpportunityServerQuery {
  return { ...query, filters: { ...query.filters, sort: undefined } };
}

/** Module-level so `useQueries` keeps the combined array stable between renders. */
function combineDocumentGroupRows(
  results: UseQueryResult<HubDocumentRow[]>[],
): { data: HubDocumentRow[] | undefined; isLoading: boolean }[] {
  return results.map((result) => ({ data: result.data, isLoading: result.isLoading }));
}
